import { infiniteQueryOptions, useInfiniteQuery, useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type {
  DepositAllSchema,
  DepositCreateSchema,
  DepositQuerySchema,
  DepositSummaryDetailAllSchema,
  DepositSummaryDetailCreateSchema,
  DepositUpdateSchema,
  PendingDepositAllSchema,
  PendingDepositItemMutationResponseSchema,
  PendingDepositItemQuerySchema,
  PendingDepositMutationSchema,
} from '@/_pages/deposits/shared/schemas';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

// Deposit APIs

export const useGetDeposit = (id: string) =>
  useSuspenseQuery({
    queryKey: ['DEPOSIT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: DepositQuerySchema }>({
        endpoint: `/api/v1/deposits/${id}`,
        params:
          'include=bank_receipt,deposit_slip,bank_account,depositor.profile_image,deposit_summary_details.client.logo,deposit_summary_details.payment_transaction.payment_method,audits',
        preventAuditLog: true,
      });
      return res;
    },
  });

export const useGetDeposits = (apiParamStr: string) =>
  useSuspenseQuery({
    queryKey: ['DEPOSITS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<DepositAllSchema>({
        endpoint: '/api/v1/deposits',
        params: `${apiParamStr}&include=deposit_slip,bank_account.brand.icon,depositor.profile_image,deposit_summary_details.client,audits`,
      });
      return res;
    },
  });

export const useCreateDepositMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, DepositCreateSchema>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: '/api/v1/deposits',
        payload,
      });
    },
  });

// Deposit Summary Details APIs

const depositSummaryDetailsStaleTimeMs = 5 * 60 * 1000;

export function getDepositSummaryDetailsForTableQuery(depositId: string) {
  return {
    queryKey: ['DEPOSIT_SUMMARY_DETAILS_TABLE', depositId],
    staleTime: depositSummaryDetailsStaleTimeMs,
    queryFn: async () => {
      const res = await apiClient.get<DepositSummaryDetailAllSchema>({
        endpoint: '/api/v1/deposit-summary-details',
        params: `filter[deposit_id]=${depositId}&include=client.logo,payment_transaction.payment_method`,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export const useBulkCreateDepositSummaryDetailsMutation = () =>
  useMutation<{ data: { id: number }[] }, ApiError, DepositSummaryDetailCreateSchema[]>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: '/api/v1/deposit-summary-details/bulk-store',
        payload: { items: payload },
      });
    },
  });

// Pending Deposits API

const pendingDepositListIncludeParams = 'include=client.logo,client.address,client.address.state,brand.icon';
const pendingDepositDetailIncludeParams = `${pendingDepositListIncludeParams},payment_method`;

export const useGetPendingDeposits = (apiParamStr: string) =>
  useSuspenseQuery({
    queryKey: ['PENDING_DEPOSITS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<PendingDepositAllSchema>({
        endpoint: '/api/v1/pending-deposit-queue',
        params: `${apiParamStr}&${pendingDepositListIncludeParams}`,
      });
      return res;
    },
  });

export function getPendingDepositsInfiniteQueryOptions(apiParamStr: string) {
  return infiniteQueryOptions<PendingDepositAllSchema>({
    queryKey: ['PENDING_DEPOSITS', 'new', apiParamStr],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiClient.get<PendingDepositAllSchema>({
        endpoint: '/api/v1/pending-deposit-queue',
        params: `${apiParamStr}&page=${pageParam}&${pendingDepositListIncludeParams}`,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
  });
}

export function useGetPendingDepositsInfinite(apiParamStr: string, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    ...getPendingDepositsInfiniteQueryOptions(apiParamStr),
    enabled: options?.enabled ?? Boolean(apiParamStr),
  });
}

export const useGetFilteredPendingDepositsMutation = () =>
  useMutation<PendingDepositAllSchema, ApiError, string>({
    mutationFn: async (apiParamStr) => {
      return queryClient.fetchQuery({
        queryKey: ['PENDING_DEPOSITS_FILTERED', apiParamStr],
        staleTime: 0,
        queryFn: async () => {
          const res = await apiClient.get<PendingDepositAllSchema>({
            endpoint: '/api/v1/pending-deposit-queue',
            params: `${apiParamStr}&${pendingDepositListIncludeParams}`,
          });
          return res;
        },
      });
    },
  });

export const useGetPendingDeposit = (id: string) => useSuspenseQuery(getPendingDepositQuery(id));

export function getPendingDepositQuery(id: string) {
  return {
    queryKey: ['PENDING_DEPOSIT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PendingDepositItemQuerySchema }>({
        endpoint: `/api/v1/pending-deposit-queue/${id}`,
        params: pendingDepositDetailIncludeParams,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export const useUpdatePendingDepositMutation = () =>
  useMutation<
    { data: PendingDepositItemMutationResponseSchema },
    ApiError,
    { id: number; payload: PendingDepositMutationSchema }
  >({
    mutationFn: async ({ id, payload }) => {
      return apiClient.request({
        endpoint: `/api/v1/pending-deposit-queue/${id}`,
        method: 'PUT',
        payload,
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSITS'] });
      queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSIT', `${id}`] });
    },
  });

export const useUpdatePendingDepositProfileMutation = (id: string) =>
  useMutation<{ data: PendingDepositItemMutationResponseSchema }, ApiError, PendingDepositMutationSchema>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: `/api/v1/pending-deposit-queue/${id}`,
        method: 'PUT',
        payload,
      });
    },
    onSuccess: () => {
      invalidateQueryAllPendingDepositsObject();
      queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSIT', id] });
    },
  });

export const useDeletePendingDepositMutation = () =>
  useMutation<unknown, ApiError, { id: string }>({
    mutationKey: ['PENDING_DEPOSIT_DELETE'],
    mutationFn: async ({ id }) => {
      return apiClient.request({
        endpoint: '/api/v1/pending-deposit-queue/bulk-delete',
        method: 'POST',
        payload: { items: [Number(id)] },
      });
    },
    onSuccess: () => {
      // Don't touch the ['PENDING_DEPOSIT', id] query here: the profile page's
      // useSuspenseQuery is still an active observer for it at this point (navigation
      // away hasn't happened yet), so invalidating/removing it would trigger an
      // immediate refetch against the now-deleted resource (404).
      invalidatePendingDeposits();
    },
  });

export const useBulkDeletePendingDepositsMutation = () =>
  useMutation<unknown, ApiError, { items: number[] }>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: '/api/v1/pending-deposit-queue/bulk-delete',
        method: 'POST',
        payload,
      });
    },
    onSuccess: (_, { items }) => {
      items.forEach((itemId) => {
        queryClient.removeQueries({ queryKey: ['PENDING_DEPOSIT', `${itemId}`] });
      });
      invalidatePendingDeposits();
    },
  });

// PDF Generation APIs

export const useGenerateRawDepositTicketMutation = (id: string) =>
  useMutation<{ data: { name: string; pdf_url: string } }, ApiError>({
    mutationFn: async () => {
      // TODO: Replace with real API call
      return { data: { name: `deposit-${id}-raw.pdf`, pdf_url: `#deposit-${id}-raw-pdf` } };
    },
  });

export const useGenerateFullDepositTicketMutation = (id: string) =>
  useMutation<{ data: { name: string; pdf_url: string } }, ApiError>({
    mutationFn: async () => {
      // TODO: Replace with real API call
      return { data: { name: `deposit-${id}-full.pdf`, pdf_url: `#deposit-${id}-full-pdf` } };
    },
  });

// Update and Delete mutations

export const useUpdateDepositMutation = (id: string) =>
  useMutation<{ data: DepositQuerySchema }, ApiError, DepositUpdateSchema>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: `/api/v1/deposits/${id}`,
        method: 'PUT',
        payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['DEPOSIT', id] });
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'deposits', id] });
    },
  });

export const useUpdateDepositStatusMutation = () =>
  useMutation<{ data: DepositQuerySchema }, ApiError, Pick<DepositUpdateSchema, 'id' | 'status'>>({
    mutationFn: async ({ id, status }) => {
      return apiClient.request({
        endpoint: `/api/v1/deposits/${id}`,
        method: 'PUT',
        payload: { id, status },
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['DEPOSITS'] });
      queryClient.invalidateQueries({ queryKey: ['DEPOSIT', `${id}`] });
    },
  });

export const useDeleteDepositMutation = () =>
  useMutation<unknown, ApiError, { id: string }>({
    mutationKey: ['DEPOSIT_DELETE'],
    mutationFn: async ({ id }) => {
      return apiClient.request({
        endpoint: '/api/v1/deposits/bulk-delete',
        method: 'POST',
        payload: { items: [Number(id)] },
      });
    },
    onSuccess: () => {
      // Don't touch the ['DEPOSIT', id] query here: the profile page's useSuspenseQuery
      // is still an active observer for it at this point (navigation away hasn't happened
      // yet), so invalidating/removing it would trigger an immediate refetch against the
      // now-deleted resource (404).
      invalidateDeposits();
    },
  });

export const useBulkDeleteDepositsMutation = () =>
  useMutation<unknown, ApiError, { items: number[] }>({
    mutationFn: async (payload) => {
      return apiClient.request({
        endpoint: '/api/v1/deposits/bulk-delete',
        method: 'POST',
        payload,
      });
    },
    onSuccess: (_, { items }) => {
      items.forEach((itemId) => {
        queryClient.removeQueries({ queryKey: ['DEPOSIT', `${itemId}`] });
      });
      invalidateDeposits();
    },
  });

// Invalidation helpers

export const invalidateDeposits = () => {
  queryClient.invalidateQueries({ queryKey: ['DEPOSITS'] });
};

export const invalidatePendingDeposits = () => {
  queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSITS'] });
};

export const invalidateQueryAllPendingDepositsObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSITS'] });
  queryClient.invalidateQueries({ queryKey: ['PENDING_DEPOSIT'] });
};
